import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightbulb, Banknote, Compass, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";

type Intent = "idea" | "funding" | "explore";

const INTENTS: { value: Intent; icon: typeof Lightbulb; title: string; body: string }[] = [
  {
    value: "idea",
    icon: Lightbulb,
    title: "I have an idea",
    body: "Post it to the board, gather support, and turn it into a fundable project.",
  },
  {
    value: "funding",
    icon: Banknote,
    title: "I'm seeking funding",
    body: "Run the Coach to get matched with grants, partners, and the right next steps.",
  },
  {
    value: "explore",
    icon: Compass,
    title: "I want to explore",
    body: "Browse what's happening in Alabama — bills, ideas, partners, and news.",
  },
];

const Welcome = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { row, needsWelcome, loading: onboardingLoading, refetch } =
    useOnboardingStatus();

  const [intent, setIntent] = useState<Intent | null>(null);
  const [county, setCounty] = useState("");
  const [focusArea, setFocusArea] = useState("");
  const [org, setOrg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  // Already completed → bounce to their natural starting point.
  useEffect(() => {
    if (!onboardingLoading && user && !needsWelcome && row) {
      const dest =
        row.intent === "funding"
          ? "/coach"
          : row.intent === "idea"
            ? "/ideas?new=1"
            : "/ideas";
      navigate(dest, { replace: true });
    }
  }, [onboardingLoading, needsWelcome, row, user, navigate]);

  useEffect(() => {
    document.title = "Welcome | The Emerging Market";
  }, []);

  const submit = async () => {
    if (!intent || !user) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("user_onboarding")
      .upsert(
        {
          user_id: user.id,
          intent,
          county: county.trim() || null,
          focus_area: focusArea.trim() || null,
          organization: org.trim() || null,
          completed_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );

    if (error) {
      setSubmitting(false);
      toast.error(error.message);
      return;
    }

    // Mirror org/county into profile if provided.
    if (org.trim() || county.trim()) {
      await supabase
        .from("profiles")
        .update({
          organization: org.trim() || undefined,
          location: county.trim() || undefined,
        })
        .eq("user_id", user.id);
    }

    // Fire-and-forget welcome email.
    supabase.functions
      .invoke("send-transactional-email", {
        body: {
          templateName: "welcome",
          recipientEmail: user.email,
          idempotencyKey: `welcome-${user.id}`,
          templateData: {
            name:
              (user.user_metadata as any)?.full_name?.split(" ")[0] ?? undefined,
          },
        },
      })
      .catch(() => {});

    await refetch();
    setSubmitting(false);

    const dest =
      intent === "funding"
        ? "/coach"
        : intent === "idea"
          ? "/ideas?new=1"
          : "/ideas";
    navigate(dest, { replace: true });
  };

  if (authLoading || !user || onboardingLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const firstName =
    (user.user_metadata as any)?.full_name?.split(" ")[0] ?? user.email?.split("@")[0];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="container mx-auto max-w-3xl px-6 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-3">
            Welcome aboard
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] mb-3">
            Glad you're here{firstName ? `, ${firstName}` : ""}.
          </h1>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl">
            We'll tailor the rest of your experience around what brings you to
            The Emerging Market. Pick one — you can always change later.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          {INTENTS.map(({ value, icon: Icon, title, body }) => {
            const active = intent === value;
            return (
              <button
                key={value}
                onClick={() => setIntent(value)}
                className={`text-left rounded-2xl border p-5 transition-all ${
                  active
                    ? "border-primary bg-primary/5 shadow-card"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <Icon
                  className={`h-5 w-5 mb-3 ${active ? "text-primary" : "text-muted-foreground"}`}
                />
                <div className="font-display font-semibold mb-1">{title}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">
                  {body}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-10 bg-card border border-border rounded-2xl p-6 md:p-7">
          <h2 className="font-display text-lg font-semibold mb-1">
            A little about you
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            Optional — all three help us match ideas, partners, and grants.
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Alabama county</Label>
              <Input
                value={county}
                onChange={(e) => setCounty(e.target.value)}
                placeholder="e.g. Mobile"
                maxLength={100}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Focus area</Label>
              <Input
                value={focusArea}
                onChange={(e) => setFocusArea(e.target.value)}
                placeholder="e.g. Health & Wellness"
                maxLength={100}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Organization</Label>
              <Input
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder="Your team or nonprofit"
                maxLength={200}
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            Heads up: full access is invite-only during private beta. You'll
            still be able to finish setup and we'll email you the moment your
            seat opens.
          </p>
          <Button
            size="lg"
            disabled={!intent || submitting}
            onClick={submit}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-semibold rounded-full px-6"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Welcome;